import { useState, useEffect, useRef, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { RefundHoldsTab } from "@/components/refund-holds-tab";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  Search,
  Undo2,
  AlertTriangle,
  CheckCircle2,
  Truck,
  Mail,
  BookCheck,
  ChevronLeft,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Trash2,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// ── Shared helpers ────────────────────────────────────────────────────────────

const gbp = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);

const fmtDate = (s: string | null | undefined) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-GB"); } catch { return s; }
};

const makeIdempotencyKey = (): string => {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `rf-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface RefundableLine {
  invoice: string;
  orderLine: number;
  part: string;
  description: string;
  qty: number;
  unitGross: number;
  lineGross: number;
  qtyRefunded: number;
  qtyRemaining: number;
}

interface PriorRefund {
  id: number;
  creditNoteRef: string | null;
  refundType: string;
  refundReason: string;
  amount: number;
  includesDelivery: boolean;
  status: string;
  createdBy: string | null;
  createdAt: string;
  stripeRefundId: string | null;
  failureMessage: string | null;
  merlinPostingStatus: string | null;
  merlinPostingError: string | null;
  merlinPostingCompletedAt: string | null;
}

interface OrderContext {
  refundOnHold?: boolean;
  refundHoldReason?: string | null;
  orderNumber: string;
  charges: Array<{ stripeTxnId: string; amount: number; currency: string; created: string }>;
  chargeTotal: number;
  invoices: string[];
  account: string | null;
  lines: RefundableLine[];
  deliveryGross: number;
  deliveryRefunded: boolean;
  refundedTotal: number;
  remainingRefundable: number;
  priorRefunds: PriorRefund[];
  suggestedEmail: string | null;
}

interface PostResult {
  ok: boolean;
  refundId: number;
  amount: number;
  stripeRefundId: string | null;
  status: "succeeded" | "pending";
  replayed: boolean;
  emailSent: boolean;
  emailError: string | null;
}

type RefundType = "full" | "partial" | "delivery";

interface CnQueueItem {
  refundOnHold?: boolean;
  refundHoldReason?: string | null;
  invoice: string;
  total: number;
  delAccount: string;
  ref: string;
  dateCreated: string | null;
  automaticWaitUntil?: string | null;
  automaticReviewReason?: string | null;
  stripeRefundSucceeded: boolean;
  merlinPostingStatus: string | null;
  merlinPostingError: string | null;
}

interface BackorderItem {
  refundOnHold?: boolean;
  refundHoldReason?: string | null;
  salesOrder: string;
  delAccount: string;
  webref2: string;
  dateCreated: string | null;
  orderValue: number;
  orderNotes: string;
  termsCode: string;
  paymentProvider: "stripe" | "adflex";
}

interface CnResolveResult {
  orderNumber: string | null;
  invoiceRef: string | null;
  method: "ref_parse" | "parts_match" | "not_found";
  replacementOrder?: string | null;
}

interface CnRefundStatus {
  cnRef: string;
  orderNumber: string;
  chargeTotal: number;
  refundedAmount: number;
  remainingRefundable: number;
  merlinPostingStatus: string | null;
  merlinPostingError: string | null;
}

interface CnPostResult {
  ok: boolean;
  account: string;
  customerid: string;
  invoiceRef: string | null;
  sltranid: string;
  amount: number;
  reference: string;
  code: string;
  message: string;
}

// ── CN → order number localStorage cache ─────────────────────────────────────
// Stores a mapping of {[cnRef]: orderNumber} so that the queue page can show
// completed/partial status on reload without re-resolving every CN via Merlin.

const CN_ORDER_CACHE_KEY = "stripe-cn-order-cache";

function readCnOrderCache(): Record<string, string> {
  try {
    const raw = localStorage.getItem(CN_ORDER_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function saveCnOrderToCache(cnRef: string, orderNumber: string): void {
  try {
    const cache = readCnOrderCache();
    cache[cnRef.toUpperCase()] = orderNumber;
    localStorage.setItem(CN_ORDER_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage unavailable (private mode etc.) — silently ignore
  }
}

// ── Dismissed CN localStorage set ────────────────────────────────────────────
// Admins can dismiss all current CNs from the queue. Dismissals persist in
// localStorage so they survive page reloads (CNs reappear if cache is cleared).

const CN_DISMISSED_KEY = "stripe-cn-dismissed";

function readDismissedCns(): Set<string> {
  try {
    const raw = localStorage.getItem(CN_DISMISSED_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveDismissedCns(set: Set<string>): void {
  try {
    localStorage.setItem(CN_DISMISSED_KEY, JSON.stringify([...set]));
  } catch {}
}

// ── Selected order state ──────────────────────────────────────────────────────

interface SelectedOrder {
  orderNumber: string;
  /** If this came from the CN queue: the CN ref */
  cnRef?: string;
  /** Merlin account, used to pre-fill MerlinCnPostForm */
  delAccount?: string;
  source: "cn" | "backorder" | "manual";
  refundReason?: "cancelled_backorder";
  /** Pre-fetched Stripe order context (skips the order/:orderNumber call) */
  preloadedCtx?: OrderContext;
  /** Pre-fetched CN amount from Merlin (skips the cn-lookup call) */
  preloadedCnAmount?: number;
  reviewReason?: string;
}

// ── StripeRefundForm ──────────────────────────────────────────────────────────

export function StripeRefundForm({
  initialOrderNumber,
  initialCnRef,
  preloadedCtx,
  preloadedCnAmount,
  onFullyRefunded,
  refundReason,
}: {
  initialOrderNumber?: string;
  initialCnRef?: string;
  /** Pre-fetched order context — skips the Stripe order/:orderNumber call */
  preloadedCtx?: OrderContext;
  /** Pre-fetched CN value — skips the Merlin cn-lookup call */
  preloadedCnAmount?: number;
  onFullyRefunded?: () => void;
  refundReason?: "cancelled_backorder";
}) {
  const { toast } = useToast();
  const [orderInput, setOrderInput] = useState(initialOrderNumber ?? "");
  const [ctx, setCtx] = useState<OrderContext | null>(preloadedCtx ?? null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [refundType, setRefundType] = useState<RefundType>(
    preloadedCtx
      ? (initialCnRef ? "partial" : preloadedCtx.remainingRefundable > 0 ? "full" : "partial")
      : "full",
  );
  const [qtyByLine, setQtyByLine] = useState<Record<string, string>>({});
  const [includeDelivery, setIncludeDelivery] = useState(false);
  const [deliveryAmountInput, setDeliveryAmountInput] = useState("");
  const [cnRef, setCnRef] = useState(initialCnRef ?? "");
  const [cnLookupAmount, setCnLookupAmount] = useState<number | null>(preloadedCnAmount ?? null);
  const [cnLookupError, setCnLookupError] = useState<string | null>(null);
  const [cnLookupPending, setCnLookupPending] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [result, setResult] = useState<PostResult | null>(null);
  const onFullyRefundedRef = useRef(onFullyRefunded);
  onFullyRefundedRef.current = onFullyRefunded;

  // If we were given pre-fetched context that shows nothing left to refund,
  // fire the callback once on mount so the queue can mark the CN done.
  const didFireFullyRefundedForPreload = useRef(false);
  useEffect(() => {
    if (
      preloadedCtx &&
      preloadedCtx.remainingRefundable <= 0 &&
      !didFireFullyRefundedForPreload.current
    ) {
      didFireFullyRefundedForPreload.current = true;
      onFullyRefundedRef.current?.();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate suggestedEmail when ctx comes from preload (no mutation onSuccess runs).
  useEffect(() => {
    if (preloadedCtx && emailTo === "") {
      setEmailTo(preloadedCtx.suggestedEmail ?? "");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForNewLookup = () => {
    setCtx(null);
    setLookupError(null);
    setRefundType("full");
    setQtyByLine({});
    setIncludeDelivery(false);
    setDeliveryAmountInput("");
    if (!initialCnRef) setCnRef("");
    setCnLookupAmount(null);
    setCnLookupError(null);
    setCnLookupPending(false);
    setEmailTo("");
    setConfirming(false);
    setIdempotencyKey(null);
    setResult(null);
  };

  const invalidateConfirm = () => {
    setConfirming(false);
    setIdempotencyKey(null);
    setResult(null);
  };

  const lookupCnForRefund = async (ref: string) => {
    const r = ref.trim();
    if (!r || !ctx?.account) { setCnLookupAmount(null); setCnLookupError(null); return; }
    setCnLookupPending(true);
    setCnLookupError(null);
    setCnLookupAmount(null);
    try {
      const res = await fetch(
        `${BASE}/api/stripe-refunds/cn-lookup?account=${encodeURIComponent(ctx.account)}&ref=${encodeURIComponent(r)}`,
        { credentials: "include" },
      );
      const body = (await res.json().catch(() => ({}))) as { invoiceTotal?: number; error?: string };
      if (!res.ok) { setCnLookupError(body.error || "CN lookup failed"); return; }
      const total = body.invoiceTotal ?? 0;
      if (total >= 0) { setCnLookupError(`${r} is not a credit note (total is not negative)`); return; }
      setCnLookupAmount(Math.abs(total));
    } catch {
      setCnLookupError("CN lookup failed");
    } finally {
      setCnLookupPending(false);
    }
  };

  const lookupMutation = useMutation({
    mutationFn: async (): Promise<OrderContext> => {
      const res = await fetch(
        `${BASE}/api/stripe-refunds/order/${encodeURIComponent(orderInput.trim())}` +
          `${cnRef.trim() ? `?cnRef=${encodeURIComponent(cnRef.trim())}` : ""}`,
        { credentials: "include" },
      );
      const body = (await res.json().catch(() => ({}))) as OrderContext & { error?: string };
      if (!res.ok) throw new Error(body?.error || "Order lookup failed");
      return body;
    },
    onSuccess: (data) => {
      setCtx(data);
      setLookupError(null);
      setEmailTo(data.suggestedEmail ?? "");
      // CN flow: always use partial so the CN amount drives the refund value.
      // Regular flow: start on full if there's anything left to refund.
      setRefundType(initialCnRef ? "partial" : data.remainingRefundable > 0 ? "full" : "partial");
      if (data.remainingRefundable <= 0) {
        onFullyRefundedRef.current?.();
      }
    },
    onError: (err: any) => {
      setCtx(null);
      setLookupError(err?.message || "Order lookup failed");
    },
  });

  // Auto-trigger lookup when mounted with an initialOrderNumber.
  // Skipped when preloadedCtx is available — ctx is already initialised.
  const didAutoLookup = useRef(false);
  useEffect(() => {
    if (preloadedCtx) return; // already have ctx from prefetch cache
    if (initialOrderNumber && !didAutoLookup.current) {
      didAutoLookup.current = true;
      lookupMutation.mutate();
    }
    // Also auto-lookup the CN ref once we have the order context
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Once order context is loaded and we have an initialCnRef, auto-lookup the CN amount.
  // Skipped when preloadedCnAmount is already available.
  const didCnLookup = useRef(false);
  useEffect(() => {
    if (ctx && initialCnRef && !didCnLookup.current) {
      didCnLookup.current = true;
      if (preloadedCnAmount != null) {
        // Already populated from the prefetch cache — no network call needed.
        setCnLookupAmount(preloadedCnAmount);
      } else {
        lookupCnForRefund(initialCnRef);
      }
    }
  }, [ctx]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedLines = ctx
    ? ctx.lines
        .map((l) => {
          const raw = qtyByLine[`${l.invoice}#${l.orderLine}`] ?? "";
          const qty = Number(raw);
          return { line: l, qty: raw.trim() === "" ? 0 : qty, raw };
        })
        .filter((s) => s.raw.trim() !== "")
    : [];

  const lineErrors = selectedLines
    .filter(
      (s) =>
        !Number.isFinite(s.qty) ||
        s.qty <= 0 ||
        !Number.isInteger(s.qty) ||
        s.qty > s.line.qtyRemaining,
    )
    .map((s) => s.line.part);

  const partialLinesAmount = selectedLines.reduce((sum, s) => {
    if (!Number.isFinite(s.qty) || s.qty <= 0) return sum;
    const amt =
      s.qty >= s.line.qty ? s.line.lineGross : Math.round(s.line.unitGross * s.qty * 100) / 100;
    return sum + amt;
  }, 0);

  const deliveryAvailable = !!ctx && ctx.deliveryGross > 0 && !ctx.deliveryRefunded;

  const deliveryAmountParsed = deliveryAmountInput.trim() === ""
    ? (ctx?.deliveryGross ?? 0)
    : Math.round(Number(deliveryAmountInput) * 100) / 100;
  const deliveryAmountValid =
    Number.isFinite(deliveryAmountParsed) &&
    deliveryAmountParsed > 0 &&
    !!ctx &&
    deliveryAmountParsed <= ctx.deliveryGross + 1e-9 &&
    deliveryAmountParsed <= ctx.remainingRefundable + 1e-9;

  const partialBaseAmount = cnLookupAmount !== null ? cnLookupAmount : partialLinesAmount;

  const previewAmount = !ctx
    ? 0
    : refundType === "full"
      ? ctx.remainingRefundable
      : refundType === "delivery"
        ? (deliveryAmountValid ? deliveryAmountParsed : 0)
        : Math.round(
            (partialBaseAmount + (includeDelivery && deliveryAvailable ? ctx.deliveryGross : 0)) * 100,
          ) / 100;

  const canonicalCnRef = cnRef.trim().toUpperCase();
  const priorCnConflict =
    ctx && canonicalCnRef && previewAmount > 0
      ? ctx.priorRefunds.find(
          (refund) =>
            (refund.status === "pending" || refund.status === "succeeded") &&
            (
              refund.creditNoteRef?.trim().toUpperCase() === canonicalCnRef ||
              (!refund.creditNoteRef && Math.abs(refund.amount - previewAmount) < 0.005)
            ),
        ) ?? null
      : null;
  const overCap = !!ctx && previewAmount > ctx.remainingRefundable + 1e-9;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTo.trim());
  const cnRefEnteredAndPending = cnRef.trim() !== "" && (cnLookupPending || (cnLookupAmount === null && !cnLookupError));
  const cnRefEnteredAndBad    = cnRef.trim() !== "" && cnLookupError !== null;

  const canSubmit =
    !!ctx &&
    !ctx.refundOnHold &&
    ctx.remainingRefundable > 0 &&
    !result &&
    !priorCnConflict &&
    emailValid &&
    !overCap &&
    (refundType === "full"
      ? true
      : refundType === "delivery"
        ? deliveryAvailable && deliveryAmountValid
        : lineErrors.length === 0 &&
          (selectedLines.some((s) => s.qty > 0) || (includeDelivery && deliveryAvailable) || cnLookupAmount !== null) &&
          previewAmount > 0 &&
          !cnRefEnteredAndPending &&
          !cnRefEnteredAndBad);

  const postMutation = useMutation({
    mutationFn: async (): Promise<PostResult> => {
      const res = await fetch(`${BASE}/api/stripe-refunds`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          orderNumber: ctx!.orderNumber,
          refundType,
          refundReason: refundReason ?? (cnRef.trim() ? "credit_note" : "operator"),
          lines:
            refundType === "partial"
              ? selectedLines
                  .filter((s) => s.qty > 0)
                  .map((s) => ({ invoice: s.line.invoice, orderLine: s.line.orderLine, qty: s.qty }))
              : [],
          includeDelivery: refundType === "partial" ? includeDelivery : true,
          deliveryOverrideAmount:
            refundType === "delivery" && deliveryAmountInput.trim() !== ""
              ? deliveryAmountParsed
              : undefined,
          cnRef: refundType === "partial" && cnRef.trim() ? cnRef.trim() : undefined,
          idempotencyKey,
          emailTo: emailTo.trim(),
        }),
      });
      const body = (await res.json().catch(() => ({}))) as PostResult & { error?: string };
      if (!res.ok) throw new Error(body?.error || "Refund failed");
      return body;
    },
    onSuccess: (data) => {
      setResult(data);
      setConfirming(false);
      setIdempotencyKey(null);
      toast({
        title: data.status === "succeeded" ? "Refund issued" : "Refund pending",
        description:
          data.status === "succeeded"
            ? `${gbp(data.amount)} refunded for order ${ctx?.orderNumber}.`
            : `${gbp(data.amount)} is pending in Stripe. Another refund is blocked.`,
      });
      lookupMutation.mutate();
      setQtyByLine({});
      setIncludeDelivery(false);
    },
    onError: (err: any) => {
      toast({
        title: "Refund failed",
        description: err?.message || "Something went wrong",
        variant: "destructive",
      });
      lookupMutation.mutate();
    },
  });

  const startConfirm = () => {
    setIdempotencyKey((k) => k ?? makeIdempotencyKey());
    setConfirming(true);
  };

  return (
    <div className="max-w-3xl space-y-6">
      {/* Lookup (hidden when order is pre-supplied and loaded) */}
      {(!initialOrderNumber || lookupError) && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Order number, web reference or invoice
          </label>
          <p className="text-xs text-slate-500 mb-2">
            Enter the Duffells order number (e.g. <span className="font-mono">001677058</span>), the
            web order reference (e.g. <span className="font-mono">1677058</span>), or any invoice on
            the order (e.g. <span className="font-mono">I3302671</span>).
          </p>
          <div className="flex gap-2">
            <input
              value={orderInput}
              onChange={(e) => {
                setOrderInput(e.target.value);
                resetForNewLookup();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && orderInput.trim()) lookupMutation.mutate();
              }}
              placeholder="e.g. 001677058, 1677058 or I3302671"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="input-order-number"
            />
            <Button
              onClick={() => lookupMutation.mutate()}
              disabled={!orderInput.trim() || lookupMutation.isPending}
              data-testid="button-lookup"
            >
              {lookupMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              <span className="ml-1.5">Look up</span>
            </Button>
          </div>
          {lookupError && (
            <div className="mt-3 flex items-start gap-2 text-sm text-red-600" data-testid="text-lookup-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              {lookupError}
            </div>
          )}
        </div>
      )}

      {/* Loading indicator when auto-looking up */}
      {initialOrderNumber && !ctx && !lookupError && lookupMutation.isPending && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-3 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Looking up order {initialOrderNumber}…
        </div>
      )}

      {ctx && (
        <>
          {ctx.refundOnHold && (
            <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              <strong>Refund on hold — human review required.</strong> {ctx.refundHoldReason}
              <p className="mt-1">Review this customer in the On hold bucket. Refunds cannot proceed while the customer stop flag is not 0.</p>
            </div>
          )}
          {/* Order summary */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <div>
                <div className="text-slate-500">Order</div>
                <div className="font-semibold text-slate-900 font-mono" data-testid="text-order-number">{ctx.orderNumber}</div>
              </div>
              <div>
                <div className="text-slate-500">Account</div>
                <div className="font-semibold text-slate-900">{ctx.account ?? "—"}</div>
              </div>
              <div>
                <div className="text-slate-500">Invoices</div>
                <div className="font-semibold text-slate-900">{ctx.invoices.join(", ") || "—"}</div>
              </div>
              <div>
                <div className="text-slate-500">Paid via Stripe</div>
                <div className="font-semibold text-slate-900">{gbp(ctx.chargeTotal)}</div>
              </div>
              <div>
                <div className="text-slate-500">Already refunded</div>
                <div className="font-semibold text-slate-900">{gbp(ctx.refundedTotal)}</div>
              </div>
              <div>
                <div className="text-slate-500">Remaining refundable</div>
                <div
                  className={`font-semibold ${ctx.remainingRefundable > 0 ? "text-emerald-700" : "text-red-600"}`}
                  data-testid="text-remaining"
                >
                  {gbp(ctx.remainingRefundable)}
                </div>
              </div>
            </div>
            {ctx.remainingRefundable <= 0 && (
              <div className="mt-3 text-sm text-emerald-700 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                This order has been fully refunded — no further refunds are possible.
              </div>
            )}
          </div>

          {/* Prior refunds */}
          {ctx.priorRefunds.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Previous refunds</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="pb-1.5 font-medium">Date</th>
                    <th className="pb-1.5 font-medium">Type</th>
                    <th className="pb-1.5 font-medium">Amount</th>
                    <th className="pb-1.5 font-medium">Status</th>
                    <th className="pb-1.5 font-medium">By</th>
                  </tr>
                </thead>
                <tbody>
                  {ctx.priorRefunds.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="py-1.5">{new Date(r.createdAt).toLocaleString("en-GB")}</td>
                      <td className="py-1.5 capitalize">
                        {r.refundType}
                        {r.refundReason === "cancelled_backorder" ? " · cancelled back order" : ""}
                        {r.includesDelivery && r.refundType === "partial" ? " + delivery" : ""}
                        {r.creditNoteRef ? (
                          <span className="ml-1 text-xs font-mono text-slate-500">({r.creditNoteRef})</span>
                        ) : null}
                      </td>
                      <td className="py-1.5">{gbp(r.amount)}</td>
                      <td className="py-1.5">
                        <span
                          className={
                            r.status === "succeeded"
                              ? "text-emerald-700"
                              : r.status === "failed"
                                ? "text-red-600"
                                : "text-amber-600"
                          }
                        >
                          {r.status}
                        </span>
                        {r.failureMessage && (
                          <span className="text-slate-400 ml-1" title={r.failureMessage}>ⓘ</span>
                        )}
                      </td>
                      <td className="py-1.5 text-slate-500">{r.createdBy ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {ctx.remainingRefundable > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              {/* Refund type — hidden for CN queue flow (always partial by CN value) */}
              {!initialCnRef && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Refund type</label>
                  <div className="flex flex-wrap gap-2">
                    {(
                      [
                        { key: "full", label: "Full refund" },
                        { key: "partial", label: "Partial refund" },
                        { key: "delivery", label: "Delivery charge only" },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => {
                          setRefundType(opt.key);
                          if (opt.key === "delivery" && ctx) setDeliveryAmountInput(ctx.deliveryGross.toFixed(2));
                          invalidateConfirm();
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm border ${
                          refundType === opt.key
                            ? "bg-primary text-white border-primary"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        }`}
                        data-testid={`button-type-${opt.key}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {refundType === "delivery" && !deliveryAvailable && (
                    <p className="mt-2 text-sm text-red-600">
                      {ctx.deliveryGross <= 0
                        ? "This order has no delivery charge."
                        : "The delivery charge has already been refunded."}
                    </p>
                  )}
                </div>
              )}

              {/* CN queue: simple fixed-amount display */}
              {initialCnRef && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 flex items-center gap-3 text-sm">
                  <BookCheck className="w-4 h-4 text-slate-400 shrink-0" />
                  <div>
                    <span className="text-slate-600">Refunding credit note </span>
                    <span className="font-mono font-semibold text-slate-900">{initialCnRef}</span>
                    {cnLookupPending && (
                      <span className="ml-3 text-slate-400 flex items-center gap-1 inline-flex">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Looking up CN value…
                      </span>
                    )}
                    {cnLookupAmount !== null && !cnLookupPending && (
                      <span className="ml-3 font-semibold text-slate-900">{gbp(cnLookupAmount)}</span>
                    )}
                    {cnLookupError && !cnLookupPending && (
                      <span className="ml-3 text-red-600">{cnLookupError}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Partial line picker — not shown in CN queue flow */}
              {!initialCnRef && refundType === "partial" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Items to refund</label>
                  {ctx.lines.length === 0 ? (
                    <p className="text-sm text-slate-500">
                      No invoice lines found for this order — only full or delivery refunds are possible.
                    </p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-500">
                          <th className="pb-1.5 font-medium">Part</th>
                          <th className="pb-1.5 font-medium">Description</th>
                          <th className="pb-1.5 font-medium text-right">Unit (inc. VAT)</th>
                          <th className="pb-1.5 font-medium text-right">Ordered</th>
                          <th className="pb-1.5 font-medium text-right">Refundable</th>
                          <th className="pb-1.5 font-medium text-right w-24">Refund qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ctx.lines.map((l) => {
                          const key = `${l.invoice}#${l.orderLine}`;
                          const exhausted = l.qtyRemaining <= 0;
                          return (
                            <tr key={key} className={`border-t border-slate-100 ${exhausted ? "opacity-50" : ""}`}>
                              <td className="py-1.5 font-medium">{l.part}</td>
                              <td className="py-1.5 text-slate-600">{l.description}</td>
                              <td className="py-1.5 text-right">{gbp(l.unitGross)}</td>
                              <td className="py-1.5 text-right">{l.qty}</td>
                              <td className="py-1.5 text-right">
                                {exhausted ? <span className="text-red-500">refunded</span> : l.qtyRemaining}
                              </td>
                              <td className="py-1.5 text-right">
                                <input
                                  type="number"
                                  min={0}
                                  max={l.qtyRemaining}
                                  step={1}
                                  disabled={exhausted}
                                  value={qtyByLine[key] ?? ""}
                                  onChange={(e) => {
                                    setQtyByLine((prev) => ({ ...prev, [key]: e.target.value }));
                                    invalidateConfirm();
                                  }}
                                  className="w-20 border border-slate-300 rounded-md px-2 py-1 text-sm text-right disabled:bg-slate-50"
                                  data-testid={`input-qty-${l.invoice}-${l.orderLine}`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {lineErrors.length > 0 && (
                    <p className="mt-2 text-sm text-red-600">
                      Invalid quantity for: {lineErrors.join(", ")} (whole numbers up to the refundable quantity).
                    </p>
                  )}

                  {/* CN ref (manual flow only) */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Credit note ref
                      <span className="ml-1.5 text-xs font-normal text-slate-400">
                        optional — when set the CN value is refunded instead of the item prices
                      </span>
                    </label>
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <input
                        value={cnRef}
                        onChange={(e) => {
                          setCnRef(e.target.value);
                          setCnLookupAmount(null);
                          setCnLookupError(null);
                          invalidateConfirm();
                        }}
                        onBlur={(e) => lookupCnForRefund(e.target.value)}
                        placeholder="e.g. CN3323723"
                        className="w-40 border border-slate-300 rounded-md px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="input-cn-ref"
                      />
                      {cnLookupPending && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                      {cnLookupAmount !== null && !cnLookupPending && (
                        <span className="text-sm text-emerald-700 flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" />
                          CN value: <strong>{gbp(cnLookupAmount)}</strong> — this will be the refund amount
                        </span>
                      )}
                      {cnLookupError && !cnLookupPending && (
                        <span className="text-sm text-red-600">{cnLookupError}</span>
                      )}
                    </div>
                  </div>

                  <label
                    className={`mt-3 flex items-center gap-2 text-sm ${deliveryAvailable ? "text-slate-700" : "text-slate-400"}`}
                  >
                    <input
                      type="checkbox"
                      checked={includeDelivery && deliveryAvailable}
                      disabled={!deliveryAvailable}
                      onChange={(e) => { setIncludeDelivery(e.target.checked); invalidateConfirm(); }}
                      data-testid="checkbox-include-delivery"
                    />
                    <Truck className="w-4 h-4" />
                    Also refund the delivery charge ({gbp(ctx.deliveryGross)})
                    {ctx.deliveryRefunded && <span className="text-red-500">— already refunded</span>}
                  </label>
                </div>
              )}

              {refundType === "delivery" && deliveryAvailable && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    <span className="flex items-center gap-1.5">
                      <Truck className="w-4 h-4" />
                      Delivery charge to refund
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-sm">£</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={Math.min(ctx.deliveryGross, ctx.remainingRefundable)}
                      value={deliveryAmountInput}
                      placeholder={ctx.deliveryGross.toFixed(2)}
                      onChange={(e) => { setDeliveryAmountInput(e.target.value); invalidateConfirm(); }}
                      className="w-32 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      data-testid="input-delivery-amount"
                    />
                    <span className="text-sm text-slate-500">of {gbp(ctx.deliveryGross)} total carriage</span>
                  </div>
                  {deliveryAmountInput.trim() !== "" && !deliveryAmountValid && (
                    <p className="mt-1 text-sm text-red-600">
                      {deliveryAmountParsed <= 0
                        ? "Amount must be greater than zero."
                        : `Maximum refundable is ${gbp(Math.min(ctx.deliveryGross, ctx.remainingRefundable))}.`}
                    </p>
                  )}
                </div>
              )}

              {/* Customer email */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Customer email (from Stripe)
                </label>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <input
                    value={emailTo}
                    onChange={(e) => { setEmailTo(e.target.value); invalidateConfirm(); }}
                    placeholder="customer@example.com"
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    data-testid="input-email"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Pre-filled from the Stripe payment details and used for the refund confirmation.
                </p>
                {emailTo.trim() !== "" && !emailValid && (
                  <p className="mt-1 text-sm text-red-600">Enter a valid email address.</p>
                )}
              </div>

              {/* Total + submit */}
              {priorCnConflict && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      {priorCnConflict.creditNoteRef
                        ? `${canonicalCnRef} has already been refunded`
                        : `A previous ${gbp(previewAmount)} refund on this order may already cover ${canonicalCnRef}`}
                      {" "}(refund #{priorCnConflict.id}, {new Date(priorCnConflict.createdAt).toLocaleString("en-GB")}).
                      Another refund is blocked.
                    </span>
                  </div>
                </div>
              )}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                <div className="text-sm">
                  <span className="text-slate-500">Refund amount: </span>
                  <span
                    className={`text-lg font-semibold ${overCap ? "text-red-600" : "text-slate-900"}`}
                    data-testid="text-refund-amount"
                  >
                    {gbp(previewAmount)}
                  </span>
                  {overCap && (
                    <span className="ml-2 text-red-600">
                      exceeds remaining refundable {gbp(ctx.remainingRefundable)}
                    </span>
                  )}
                </div>
                {!confirming ? (
                  <Button onClick={startConfirm} disabled={!canSubmit} data-testid="button-refund">
                    <Undo2 className="w-4 h-4 mr-1.5" />
                    Refund {gbp(previewAmount)}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">
                      Refund <strong>{gbp(previewAmount)}</strong> to the customer's card?
                    </span>
                    <Button variant="outline" onClick={() => setConfirming(false)} disabled={postMutation.isPending} data-testid="button-cancel-confirm">
                      Cancel
                    </Button>
                    <Button onClick={() => postMutation.mutate()} disabled={postMutation.isPending || !canSubmit} data-testid="button-confirm-refund">
                      {postMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                      )}
                      Confirm refund
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-sm" data-testid="panel-result">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                <CheckCircle2 className="w-5 h-5" />
                Refund of {gbp(result.amount)} {result.status === "succeeded" ? "issued" : "is pending"}
                {result.stripeRefundId && (
                  <span className="font-normal text-emerald-700">({result.stripeRefundId})</span>
                )}
              </div>
              <p className="mt-1.5 text-emerald-800">
                {result.status === "pending"
                  ? "Stripe has not completed this refund yet. No confirmation email was sent, and another refund for this credit note is blocked."
                  : result.emailSent
                    ? `Confirmation email sent to ${emailTo.trim()} — the customer should see the money back within 5–10 business days.`
                    : `The refund succeeded but the confirmation email could not be sent${result.emailError ? ` (${result.emailError})` : ""}. Please contact the customer directly.`}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── MerlinCnPostForm ──────────────────────────────────────────────────────────

function MerlinCnPostForm({
  initialAccount,
  initialCnRef,
}: {
  initialAccount?: string;
  initialCnRef?: string;
}) {
  const { toast } = useToast();
  const [account, setAccount] = useState(initialAccount ?? "");
  const [cnRef, setCnRef] = useState(initialCnRef ?? "");
  const [reference, setReference] = useState("Stripe refund");
  const [confirming, setConfirming] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [result, setResult] = useState<CnPostResult | null>(null);
  const [lockedAmount, setLockedAmount] = useState<number | null>(null);
  const [cnLookupError, setCnLookupError] = useState<string | null>(null);
  const [cnLookupPending, setCnLookupPending] = useState(false);

  const lookupCnAmount = async (acc: string, ref: string) => {
    const a = acc.trim();
    const r = ref.trim();
    if (!a || !r) { setLockedAmount(null); setCnLookupError(null); return; }
    setCnLookupPending(true);
    setCnLookupError(null);
    setLockedAmount(null);
    try {
      const res = await fetch(
        `${BASE}/api/stripe-refunds/cn-lookup?account=${encodeURIComponent(a)}&ref=${encodeURIComponent(r)}`,
        { credentials: "include" },
      );
      const body = (await res.json().catch(() => ({}))) as { invoiceTotal?: number; error?: string };
      if (!res.ok) { setCnLookupError(body.error || "CN lookup failed"); return; }
      setLockedAmount(body.invoiceTotal ?? null);
    } catch {
      setCnLookupError("CN lookup failed");
    } finally {
      setCnLookupPending(false);
    }
  };

  // Auto-lookup when pre-filled with both account and CN ref
  const didAutoLookup = useRef(false);
  useEffect(() => {
    if (initialAccount && initialCnRef && !didAutoLookup.current) {
      didAutoLookup.current = true;
      lookupCnAmount(initialAccount, initialCnRef);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const parsedAmount = lockedAmount ?? 0;
  const amountValid = lockedAmount !== null && lockedAmount < -0.005;
  const canSubmit =
    account.trim().length > 0 &&
    cnRef.trim().length > 0 &&
    amountValid &&
    !cnLookupPending &&
    !cnLookupError &&
    reference.trim().length > 0;

  const resetResult = () => { setResult(null); setConfirming(false); setIdempotencyKey(null); };
  const resetLookup = () => { setLockedAmount(null); setCnLookupError(null); setCnLookupPending(false); resetResult(); };

  const postMutation = useMutation({
    mutationFn: async (): Promise<CnPostResult> => {
      const res = await fetch(`${BASE}/api/stripe-refunds/post-merlin-cn`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          account: account.trim(),
          creditNoteRef: cnRef.trim(),
          amount: parsedAmount,
          reference: reference.trim(),
          idempotencyKey,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as CnPostResult & { error?: string };
      if (!res.ok) throw new Error(body?.error || "Posting failed");
      return body;
    },
    onSuccess: (data) => {
      setResult(data);
      setConfirming(false);
      setIdempotencyKey(null);
      toast({ title: `CN posted to Merlin`, description: `${cnRef.trim()} cleared on ${account.trim()} (code ${data.code})` });
    },
    onError: (err: any) => {
      setConfirming(false);
      toast({ title: "Merlin posting failed", description: err?.message || "Something went wrong", variant: "destructive" });
    },
  });

  const startConfirm = () => { setIdempotencyKey((k) => k ?? makeIdempotencyKey()); setConfirming(true); };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
        <BookCheck className="w-4 h-4 text-slate-500" />
        Post Credit Note to Merlin
      </h2>
      <p className="text-xs text-slate-500">
        After a Stripe refund is confirmed, use this to post the corresponding credit note in Merlin and clear the balance.
        The amount is locked to the CN value fetched from Merlin.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Account</label>
          <input
            value={account}
            onChange={(e) => { setAccount(e.target.value); resetLookup(); }}
            onBlur={(e) => lookupCnAmount(e.target.value, cnRef)}
            placeholder="e.g. SAL035"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Credit note ref</label>
          <input
            value={cnRef}
            onChange={(e) => { setCnRef(e.target.value); resetLookup(); }}
            onBlur={(e) => lookupCnAmount(account, e.target.value)}
            placeholder="e.g. CN3322898"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Amount</label>
          <div
            className={`w-full border rounded-lg px-3 py-2 text-sm font-mono flex items-center gap-2 ${
              cnLookupPending
                ? "border-slate-200 bg-slate-50 text-slate-400"
                : lockedAmount !== null && amountValid
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                  : cnLookupError
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-slate-200 bg-slate-50 text-slate-400"
            }`}
          >
            {cnLookupPending ? (
              <><Loader2 className="w-3 h-3 animate-spin" /><span>Looking up…</span></>
            ) : lockedAmount !== null ? (
              gbp(lockedAmount)
            ) : cnLookupError ? (
              cnLookupError
            ) : (
              <span className="text-slate-400">Filled from Merlin after entering account &amp; ref</span>
            )}
          </div>
          {lockedAmount !== null && !amountValid && (
            <p className="mt-1 text-xs text-red-600">CN has no negative balance — cannot post.</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Reference</label>
          <input
            value={reference}
            onChange={(e) => { setReference(e.target.value); resetResult(); }}
            placeholder="e.g. Stripe refund"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>
      <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {canSubmit && (
            <span>
              Post <span className="font-semibold text-slate-900">{gbp(parsedAmount)}</span> against{" "}
              <span className="font-mono font-semibold text-slate-900">{cnRef.trim()}</span> on{" "}
              <span className="font-mono font-semibold text-slate-900">{account.trim()}</span>
            </span>
          )}
        </div>
        {!confirming ? (
          <Button onClick={startConfirm} disabled={!canSubmit || !!result} variant={result ? "outline" : "default"}>
            <BookCheck className="w-4 h-4 mr-1.5" />
            Post to Merlin
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Post <strong>{gbp(parsedAmount)}</strong> to Merlin now?</span>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={postMutation.isPending}>Cancel</Button>
            <Button onClick={() => postMutation.mutate()} disabled={postMutation.isPending || !canSubmit}>
              {postMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              Confirm
            </Button>
          </div>
        )}
      </div>
      {result && (
        <div
          className={`rounded-lg p-4 text-sm flex items-start gap-2 ${
            result.ok
              ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          {result.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
          <div>
            <p className="font-semibold">{result.ok ? "Posted successfully" : `Merlin rejected (code ${result.code})`}</p>
            <p className="mt-0.5">
              {result.message}
              {result.ok && (
                <span className="ml-2 text-emerald-700">sltranid {result.sltranid} · customerid {result.customerid}</span>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CnQueueTab ────────────────────────────────────────────────────────────────

function CnQueueTab({
  completedCns,
  resolvingCn,
  onProcess,
}: {
  completedCns: Set<string>;
  resolvingCn: string | null;
  onProcess: (item: CnQueueItem) => void;
}) {
  const [showCompleted, setShowCompleted] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(readDismissedCns);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { isRealAdmin } = useAuth();

  const { data, isLoading, error, refetch, isFetching } = useQuery<{
    items: CnQueueItem[];
    queueWarning: string | null;
  }>({
    queryKey: ["cn-queue"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stripe-refunds/cn-queue`, { credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Failed to load CN queue");
      return body;
    },
    staleTime: 2 * 60_000,
  });

  const allItems = (data?.items ?? []).filter(
    (i) => !dismissed.has(i.invoice.toUpperCase()),
  );

  // Batch status query — only runs when we have queue items with cached order numbers.
  // We read the localStorage cache once per queue load; stale is fine (60 s).
  const { data: statusData } = useQuery<{ results: CnRefundStatus[] }>({
    queryKey: ["cn-refund-status", allItems.map((i) => i.invoice).join(",")],
    queryFn: async () => {
      const cache = readCnOrderCache();
      const orders = allItems
        .filter((i) => !!cache[i.invoice.toUpperCase()])
        .map((i) => ({ cnRef: i.invoice, orderNumber: cache[i.invoice.toUpperCase()] }));
      if (orders.length === 0) return { results: [] };
      const res = await fetch(`${BASE}/api/stripe-refunds/cn-refund-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ orders }),
      });
      const body = await res.json().catch(() => ({ results: [] }));
      return body;
    },
    enabled: allItems.length > 0,
    staleTime: 60_000,
  });

  // Map cnRef → CnRefundStatus for O(1) lookups below.
  const cnStatusMap = useMemo(() => {
    const map = new Map<string, CnRefundStatus>();
    for (const r of statusData?.results ?? []) {
      map.set(r.cnRef, r);
    }
    return map;
  }, [statusData]);

  // A CN is completed when:
  //   • the operator navigated into it and saw a zero remaining balance this session, OR
  //   • the local ledger (stripe_refunds) confirms the full charge has been refunded
  const isCompleted = (cnRef: string): boolean => {
    if (completedCns.has(cnRef)) return true;
    const item = allItems.find((candidate) => candidate.invoice === cnRef);
    if (item?.stripeRefundSucceeded) return true;
    const s = cnStatusMap.get(cnRef);
    return !!s && s.chargeTotal > 0 && s.remainingRefundable <= 0.005;
  };

  // A CN is partially refunded when some (but not all) of the charge has been returned.
  const getPartialStatus = (cnRef: string): CnRefundStatus | null => {
    const s = cnStatusMap.get(cnRef);
    if (!s || s.refundedAmount <= 0.005 || s.remainingRefundable <= 0.005) return null;
    return s;
  };

  const renderMerlinException = (item: CnQueueItem, status?: CnRefundStatus) => {
    const merlinStatus = status?.merlinPostingStatus ?? item.merlinPostingStatus;
    const error = status?.merlinPostingError ?? item.merlinPostingError;
    if (merlinStatus !== "failed" && merlinStatus !== "unknown") return null;
    return (
      <span
        className="ml-2 inline-flex items-center gap-1 text-xs text-red-700"
        title={error ?? undefined}
      >
        <AlertTriangle className="w-3.5 h-3.5" />
        Merlin {merlinStatus === "unknown" ? "outcome unknown — reconcile manually" : "posting failed — recover manually"}
      </span>
    );
  };

  const pendingItems = allItems.filter((i) => !isCompleted(i.invoice) && !i.refundOnHold);
  const heldCount = allItems.filter((i) => !isCompleted(i.invoice) && i.refundOnHold).length;
  const completedItems = allItems.filter((i) => isCompleted(i.invoice));

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-8">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading credit note queue from Merlin…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">Failed to load CN queue</p>
          <p>{(error as Error).message}</p>
          <button onClick={() => refetch()} className="mt-2 underline">Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {pendingItems.length} credit note{pendingItems.length !== 1 ? "s" : ""} to review or refund
          {completedItems.length > 0 && ` · ${completedItems.length} completed`}
          {heldCount > 0 && ` · ${heldCount} on hold for human review`}
        </p>
        <div className="flex items-center gap-2">
          {isRealAdmin && allItems.length > 0 && (
            confirmDelete ? (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-red-600">Dismiss all {allItems.length} CNs?</span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    const next = new Set([
                      ...dismissed,
                      ...(data?.items ?? []).map((i) => i.invoice.toUpperCase()),
                    ]);
                    setDismissed(next);
                    saveDismissedCns(next);
                    setConfirmDelete(false);
                  }}
                >
                  Yes, dismiss all
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Dismiss all
              </Button>
            )
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {data?.queueWarning && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Live Merlin credit notes could not be loaded</p>
            <p>Refunds needing Merlin reconciliation are still shown below. {data.queueWarning}</p>
          </div>
        </div>
      )}

      {pendingItems.length === 0 && completedItems.length === 0 && heldCount === 0 && (
        <div className="text-center py-12 text-slate-400 text-sm">
          No credit notes found for the configured salesman codes.
        </div>
      )}
      {heldCount > 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Held customer refunds are excluded from this list. Open the On hold tab to review them.
        </p>
      )}

      {pendingItems.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-500">
                <th className="px-4 py-2.5 font-medium">CN Ref</th>
                <th className="px-4 py-2.5 font-medium">Account</th>
                <th className="px-4 py-2.5 font-medium">Value</th>
                <th className="px-4 py-2.5 font-medium">Reference</th>
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="px-4 py-2.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {pendingItems.map((item) => {
                const partial = getPartialStatus(item.invoice);
                return (
                  <tr key={item.invoice} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-mono font-medium text-slate-900">
                      {item.invoice}
                      {item.automaticReviewReason && (
                        <span className="ml-2 inline-flex rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-sans text-amber-800" title={item.automaticReviewReason}>
                          Needs review
                        </span>
                      )}
                      {item.automaticWaitUntil && !item.automaticReviewReason && (
                        <span className="ml-2 inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-sans text-slate-600" title={`Automatic refund waits until at least ${new Date(item.automaticWaitUntil).toLocaleString()}`}>
                          Auto refund waiting 48h
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-600">{item.delAccount || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-amber-700 font-medium">{gbp(item.total)}</span>
                      {partial && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-xs text-amber-700 font-medium whitespace-nowrap">
                          Partial — {gbp(partial.refundedAmount)} of {gbp(partial.chargeTotal)} refunded
                        </span>
                      )}
                      {renderMerlinException(item, cnStatusMap.get(item.invoice))}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 max-w-xs truncate">{item.ref || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500">{fmtDate(item.dateCreated)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button
                        size="sm"
                        onClick={() => onProcess(item)}
                        disabled={resolvingCn === item.invoice}
                      >
                        {resolvingCn === item.invoice ? (
                          <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Resolving…</>
                        ) : (
                          <><Undo2 className="w-3.5 h-3.5 mr-1.5" />Process</>
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {completedItems.length > 0 && (
        <div>
          <button
            onClick={() => setShowCompleted((v) => !v)}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
          >
            {showCompleted ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showCompleted ? "Hide" : "Show"} {completedItems.length} completed
          </button>
          {showCompleted && (
            <div className="mt-2 bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200">
                  <tr className="text-left text-slate-400">
                    <th className="px-4 py-2 font-medium">CN Ref</th>
                    <th className="px-4 py-2 font-medium">Account</th>
                    <th className="px-4 py-2 font-medium">Value</th>
                    <th className="px-4 py-2 font-medium">Reference</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {completedItems.map((item) => {
                    const s = cnStatusMap.get(item.invoice);
                    const merlinStatus = s?.merlinPostingStatus ?? item.merlinPostingStatus;
                    return (
                      <tr key={item.invoice} className="border-t border-slate-200">
                        <td className="px-4 py-2 font-mono text-slate-400">{item.invoice}</td>
                        <td className="px-4 py-2 font-mono text-slate-400">{item.delAccount || "—"}</td>
                        <td className="px-4 py-2 text-slate-400">{gbp(item.total)}</td>
                        <td className="px-4 py-2 text-slate-400 max-w-xs truncate">{item.ref || "—"}</td>
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Fully refunded
                            {s && s.chargeTotal > 0 && (
                              <span className="text-emerald-600 font-normal">
                                {" "}({gbp(s.refundedAmount)})
                              </span>
                            )}
                          </span>
                          {merlinStatus === "succeeded" && (
                            <span className="ml-2 text-xs text-emerald-700">· Merlin CN cleared</span>
                          )}
                          {merlinStatus === "posting" && (
                            <span className="ml-2 text-xs text-blue-700">· Merlin posting in progress</span>
                          )}
                          {renderMerlinException(item, s)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── BackorderTab ──────────────────────────────────────────────────────────────

function BackorderTab({
  onProcess,
}: {
  onProcess: (item: BackorderItem) => void;
}) {
  const [searchInput, setSearchInput] = useState("");
  const [submitted, setSubmitted] = useState("");

  const { data, isLoading, error } = useQuery<{ items: BackorderItem[] }>({
    queryKey: ["backorder-search", submitted],
    queryFn: async () => {
      const params = submitted ? `?q=${encodeURIComponent(submitted)}` : "";
      const res = await fetch(`${BASE}/api/stripe-refunds/backorder-search${params}`, { credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Backorder search failed");
      return body;
    },
    enabled: true, // always fetch (unfiltered loads all)
    staleTime: 60_000,
  });

  const items = (data?.items ?? []).filter((item) => !item.refundOnHold);

  const doSearch = () => setSubmitted(searchInput.trim());

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
          placeholder="Search by account (e.g. SAL035) or order number"
          className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <Button onClick={doSearch} disabled={isLoading}>
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          <span className="ml-1.5">Search</span>
        </Button>
        {submitted && (
          <Button variant="outline" onClick={() => { setSearchInput(""); setSubmitted(""); }}>
            Clear
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          {(error as Error).message}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="text-center py-12 text-slate-400 text-sm">
          {submitted
            ? `No backorders found matching "${submitted}".`
            : "No backorders found for the configured salesman codes."}
        </div>
      )}

      {items.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-500">
                <th className="px-4 py-2.5 font-medium">Order</th>
                <th className="px-4 py-2.5 font-medium">Account</th>
                <th className="px-4 py-2.5 font-medium">Web ref</th>
                <th className="px-4 py-2.5 font-medium">Route</th>
                <th className="px-4 py-2.5 font-medium">Order value</th>
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="px-4 py-2.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.salesOrder} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-mono font-medium text-slate-900">{item.salesOrder}</td>
                  <td className="px-4 py-2.5 font-mono text-slate-600">{item.delAccount || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-slate-600">{item.webref2 || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={item.paymentProvider === "stripe" ? "text-indigo-700" : "text-amber-700"}>
                      {item.paymentProvider === "stripe" ? "Stripe" : "Adflex (parked)"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">{gbp(item.orderValue)}</td>
                  <td className="px-4 py-2.5 text-slate-500">{fmtDate(item.dateCreated)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button
                      size="sm"
                      disabled={item.paymentProvider !== "stripe" || !item.webref2}
                      onClick={() => onProcess(item)}
                    >
                      <Undo2 className="w-3.5 h-3.5 mr-1.5" />
                      {item.paymentProvider === "stripe" ? "Process" : "Adflex pending"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">
            Showing {items.length} result{items.length !== 1 ? "s" : ""}
            {items.length === 200 ? " (limit reached — refine your search)" : ""}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function StripeRefundsPage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<"cn" | "backorder" | "on_hold">("cn");
  const [selectedOrder, setSelectedOrder] = useState<SelectedOrder | null>(null);
  const [completedCns, setCompletedCns] = useState<Set<string>>(new Set());
  const [resolvingCn, setResolvingCn] = useState<string | null>(null);

  const markCnCompleted = (cnRef: string) =>
    setCompletedCns((prev) => new Set([...prev, cnRef]));

  const handleProcessCn = async (item: CnQueueItem) => {
    // Resolve only when the operator chooses a CN. The queue deliberately does
    // not prefetch order contexts because each context may call Stripe once per
    // charge and can exhaust the QuotaGuard connection allowance.
    setResolvingCn(item.invoice);
    try {
      const res = await fetch(
        `${BASE}/api/stripe-refunds/cn-resolve/${encodeURIComponent(item.invoice)}?account=${encodeURIComponent(item.delAccount)}`,
        { credentials: "include" },
      );
      const body = (await res.json().catch(() => ({}))) as CnResolveResult & { error?: string };

      if (!res.ok) {
        toast({
          title: "Resolution failed",
          description: body.error || "Could not resolve this CN to a Stripe order.",
          variant: "destructive",
        });
        return;
      }

      if (!body.orderNumber) {
        toast({
          title: "No Stripe order found",
          description: `Could not match ${item.invoice} to a web order. You can process it manually by entering the order number below.`,
          variant: "destructive",
        });
        setSelectedOrder({
          orderNumber: "",
          cnRef: item.invoice,
          delAccount: item.delAccount,
          source: "cn",
        });
        return;
      }

      const methodLabel = body.method === "ref_parse"
        ? `matched via invoice ${body.invoiceRef}`
        : `matched via parts overlap with invoice ${body.invoiceRef}`;

      const reviewReason = body.replacementOrder
        ? `Replacement order ${body.replacementOrder} references ${body.invoiceRef}. Automatic refund is blocked; review the replacement before deciding whether to refund manually.`
        : undefined;
      toast({
        title: reviewReason ? "Replacement needs review" : `Order resolved: ${body.orderNumber}`,
        description: reviewReason ?? methodLabel,
        variant: reviewReason ? "destructive" : undefined,
      });
      saveCnOrderToCache(item.invoice, body.orderNumber);
      setSelectedOrder({
        orderNumber: body.orderNumber,
        cnRef: item.invoice,
        delAccount: item.delAccount,
        source: "cn",
        reviewReason,
      });
    } catch (err: any) {
      toast({
        title: "Resolution error",
        description: err?.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setResolvingCn(null);
    }
  };

  const handleProcessBackorder = (item: BackorderItem) => {
    if (item.paymentProvider !== "stripe" || !item.webref2) return;
    setSelectedOrder({
      orderNumber: item.webref2,
      source: "backorder",
      refundReason: "cancelled_backorder",
    });
  };

  const handleBack = () => setSelectedOrder(null);

  return (
    <Layout>
      <div className="mb-6 flex items-start justify-between">
        <div>
          {selectedOrder ? (
            <button
              onClick={handleBack}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-3"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to queue
            </button>
          ) : null}
          <h1 className="text-2xl font-bold text-slate-900">Stripe Refunds</h1>
          <p className="text-sm text-slate-500 mt-1">
            {selectedOrder
              ? selectedOrder.cnRef
                ? `Processing credit note ${selectedOrder.cnRef}`
                : `Processing backorder ${selectedOrder.orderNumber}`
              : "Refund credit notes and backorders via Stripe."}
          </p>
        </div>
      </div>

      {selectedOrder ? (
        /* ── Processing view ── */
        <div className="space-y-8">
          {selectedOrder.reviewReason && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" role="alert">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{selectedOrder.reviewReason}</span>
            </div>
          )}
          <StripeRefundForm
            initialOrderNumber={selectedOrder.orderNumber || undefined}
            initialCnRef={selectedOrder.cnRef}
            preloadedCtx={selectedOrder.preloadedCtx}
            preloadedCnAmount={selectedOrder.preloadedCnAmount}
            refundReason={selectedOrder.refundReason}
            onFullyRefunded={
              selectedOrder.cnRef
                ? () => markCnCompleted(selectedOrder.cnRef!)
                : undefined
            }
          />
          {selectedOrder.source === "cn" && (
            <MerlinCnPostForm
              initialAccount={selectedOrder.delAccount}
              initialCnRef={selectedOrder.cnRef}
            />
          )}
        </div>
      ) : (
        /* ── Queue view ── */
        <div className="space-y-6">
          {/* Tab bar */}
          <div className="flex gap-1 border-b border-slate-200">
            {(
              [
                { key: "cn", label: "Credit Notes" },
                { key: "backorder", label: "Backorders" },
                { key: "on_hold", label: "On hold" },
              ] as const
            ).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.key
                    ? "border-primary text-primary"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "cn" && (
            <CnQueueTab
              completedCns={completedCns}
              resolvingCn={resolvingCn}
              onProcess={handleProcessCn}
            />
          )}
          {activeTab === "backorder" && (
            <BackorderTab onProcess={handleProcessBackorder} />
          )}
          {activeTab === "on_hold" && <RefundHoldsTab />}
        </div>
      )}
    </Layout>
  );
}
